import React, { Suspense, useEffect } from "react";
import ReactDOM from "react-dom/client";
import { ErrorBoundary } from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import "~/shared/services/error-reporting";
import { ErrorFallback } from "~/components/errors/error-fallback-screen";
import { GlobalLoading } from "~/components/loading";
import { Toaster } from "~/components/ui/sonner";
import { TooltipProvider } from "~/components/ui/tooltip";
import { AuthContextProvider, useAuth } from "~/contexts/auth-context";
import { FeatureFlagProvider } from "~/contexts/feature-flag-context";
import { EmulatorBadge } from "~/integrations/firebase/emulator-badge";
import { initialiseLaunchDarkly } from "~/integrations/launch-darkly";
import { parseSearch, stringifySearch } from "~/router-search";
import { routeTree } from "~/routeTree.gen";
import "./index.css";

const queryClient = new QueryClient();

const router = createRouter({
  routeTree,
  parseSearch,
  stringifySearch,
  // Runs a route's guards and loaders when its link is hovered or focused, so
  // the navigation itself doesn't wait for them.
  defaultPreload: "intent",
  // InnerApp supplies the real value once AuthContextProvider has loaded.
  context: { auth: undefined! },
  Wrap: ({ children }) => (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  ),
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }

  interface HistoryState {
    /** The URL a detail page was opened from, for its back button. */
    from?: string;
    /** The page and query string the profile page's back button returns to. */
    prevPath?: string;
    prevSearch?: string;
  }
}

function InnerApp() {
  const auth = useAuth();
  const userId = auth.session?.user.id;
  const organisationId = auth.session?.organisation.id;
  const isSuperAdmin = auth.session?.user.isSuperAdmin;

  // Re-run the route guards only when what they check changes. The session
  // object is rebuilt on every user or organisation snapshot, and invalidating
  // reloads every route and scrolls the window to the top.
  useEffect(() => {
    router.invalidate();
  }, [userId, organisationId, isSuperAdmin]);

  return <RouterProvider router={router} context={{ auth }} />;
}

function App() {
  const { t } = useTranslation(["general"]);

  return (
    <Suspense
      fallback={<GlobalLoading label={t("general:globalLoading.app")} />}
    >
      <ErrorBoundary fallback={ErrorFallback}>
        <AuthContextProvider>
          <FeatureFlagProvider>
            <InnerApp />
          </FeatureFlagProvider>
        </AuthContextProvider>
        {/* Outside the auth and flag providers so toasts survive their loading screens */}
        <Toaster />
        <EmulatorBadge />
      </ErrorBoundary>
    </Suspense>
  );
}

const load = async () => {
  const { i18nReady } = await import("./shared/i18n");
  const LaunchDarklyProvider = await initialiseLaunchDarkly();
  await i18nReady;

  const root = ReactDOM.createRoot(document.getElementById("root")!);

  root.render(
    <React.StrictMode>
      <LaunchDarklyProvider>
        <TooltipProvider delayDuration={100}>
          <App />
        </TooltipProvider>
      </LaunchDarklyProvider>
    </React.StrictMode>,
  );
};

load().then();
